import jsPDF from 'jspdf';
import { InvoiceData } from '../types/invoice';

export class InvoicePDFGenerator {
  private doc: jsPDF;
  private data: InvoiceData;
  private pageWidth: number;
  private pageHeight: number;
  private margin: number = 10;
  private currentY: number = 10;

  constructor(data: InvoiceData) {
    this.data = data;
    this.doc = new jsPDF('p', 'mm', 'a4');
    this.pageWidth = this.doc.internal.pageSize.getWidth();
    this.pageHeight = this.doc.internal.pageSize.getHeight();
  }

  generate(): jsPDF {
    this.drawWatermark();
    this.drawBorder();
    this.drawHeader();
    this.drawInvoiceDetails();
    this.drawBillToShipTo();
    this.drawItemsTable();
    this.drawTotals();
    this.drawNotesAndBank();
    this.drawFooter();
    this.drawReceiverSection();

    return this.doc;
  }

  private drawWatermark() {
    // Save current graphics state
    const pageCenter = {
      x: this.pageWidth / 2,
      y: this.pageHeight / 2
    };

    // Set watermark style
    this.doc.setTextColor(220, 220, 220); // Light gray
    this.doc.setFontSize(60);
    this.doc.setFont('helvetica', 'bold');

    // Rotate and draw watermark text
    this.doc.saveGraphicsState();
    // @ts-ignore - GState type issue with jsPDF
    this.doc.setGState(new this.doc.GState({ opacity: 0.1 }));

    // Rotate 45 degrees around center
    const angle = -45;

    // Draw company name as watermark
    const watermarkText = this.data.companyName;
    this.doc.text(watermarkText, pageCenter.x, pageCenter.y, {
      align: 'center',
      angle: angle
    });

    this.doc.restoreGraphicsState();

    // Reset text color to black for rest of document
    this.doc.setTextColor(0, 0, 0);
  }

  private drawBorder() {
    this.doc.setLineWidth(0.5);
    this.doc.rect(
      this.margin,
      this.margin,
      this.pageWidth - 2 * this.margin,
      this.pageHeight - 2 * this.margin
    );
  }

  private drawHeader() {
    const headerY = this.margin + 5;
    const leftX = this.margin + 5;
    const rightX = this.pageWidth - this.margin - 5;

    // Company Name (left) and TAX INVOICE (right) on same line
    this.doc.setFontSize(14);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(this.data.companyName, leftX, headerY);
    this.doc.text('TAX INVOICE', rightX, headerY, { align: 'right' });

    // Company Address (left side)
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');
    const addressLines = [
      this.data.companyAddress,
      this.data.companyCity,
      `${this.data.companyState} ${this.data.companyPincode}`,
      this.data.companyCountry,
      `GSTIN ${this.data.companyGstin}`
    ];

    let addressY = headerY + 5;
    addressLines.forEach(line => {
      this.doc.text(line, leftX, addressY);
      addressY += 4;
    });

    // Draw horizontal line after header
    this.currentY = addressY + 2;
    this.doc.setLineWidth(0.3);
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
  }

  private drawInvoiceDetails() {
    this.currentY += 5;
    const leftX = this.margin + 5;
    const rightLabelX = this.pageWidth / 2 + 20;

    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');

    // Left side details
    const leftY = this.currentY;
    this.drawLabelValue('Invoice No', this.data.invoiceNo, leftX, leftY);
    this.drawLabelValue('Invoice Date', this.data.invoiceDate, leftX, leftY + 5);
    this.drawLabelValue('Terms', this.data.terms, leftX, leftY + 10);
    this.drawLabelValue('Due Date', this.data.dueDate, leftX, leftY + 15);

    // Right side - Place of Supply
    this.drawLabelValue('Place Of Supply', this.data.placeOfSupply, rightLabelX, leftY);

    this.currentY = leftY + 20;
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
  }

  private drawBillToShipTo() {
    this.currentY += 5;
    const leftX = this.margin + 5;
    const middleX = this.pageWidth / 2;
    const rightX = middleX + 5;

    const sectionTop = this.currentY;

    // Headers
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Bill To', leftX, this.currentY);
    this.doc.text('Ship To', rightX, this.currentY);

    this.currentY += 5;
    this.doc.setFont('helvetica', 'normal');

    // Bill To Address
    let billY = this.currentY;
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(this.data.billTo.name, leftX, billY);
    this.doc.setFont('helvetica', 'normal');
    billY += 5;

    if (this.data.billTo.address) {
      this.doc.text(this.data.billTo.address, leftX, billY);
      billY += 4;
    }
    this.doc.text(this.data.billTo.country || 'India', leftX, billY);
    billY += 4;
    if (this.data.billTo.gstin) {
      this.doc.text(`GSTIN ${this.data.billTo.gstin}`, leftX, billY);
      billY += 4;
    }

    // Ship To Address
    let shipY = this.currentY;
    if (this.data.shipTo.name) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text(this.data.shipTo.name, rightX, shipY);
      this.doc.setFont('helvetica', 'normal');
      shipY += 5;
    }
    if (this.data.shipTo.address) {
      this.doc.text(this.data.shipTo.address, rightX, shipY);
      shipY += 4;
    }
    this.doc.text(this.data.shipTo.country || 'India', rightX, shipY);
    shipY += 4;

    // Vertical divider
    const sectionHeight = Math.max(billY, shipY) - sectionTop + 2;
    this.doc.line(middleX, sectionTop - 5, middleX, sectionTop + sectionHeight);

    this.currentY = Math.max(billY, shipY) + 2;
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
  }

  private drawItemsTable() {
    const startY = this.currentY;
    const startX = this.margin;

    // Column configuration - Total width adjusted to fit within page (190mm available)
    const columns = [
      { header: 'S.\nNO', width: 10 },
      { header: 'Item & Description', width: 60 },
      { header: 'HSN', width: 18 },
      { header: 'Qty', width: 15 },
      { header: 'Rate', width: 18 },
      { header: 'CGST\n%', width: 10 },
      { header: 'CGST\nAmt', width: 13 },
      { header: 'SGST\n%', width: 10 },
      { header: 'SGST\nAmt', width: 13 },
      { header: 'Amount', width: 23 }
    ];

    // Calculate column X positions
    const colX: number[] = [];
    let currentX = startX;
    columns.forEach(col => {
      colX.push(currentX);
      currentX += col.width;
    });
    const tableWidth = currentX - startX;

    // Draw header row
    const headerHeight = 10;
    this.doc.setFillColor(240, 240, 240);
    this.doc.rect(startX, this.currentY, tableWidth, headerHeight, 'F');

    this.doc.setFontSize(7.2);
    this.doc.setFont('helvetica', 'bold');

    columns.forEach((col, i) => {
      const lines = col.header.split('\n');
      const lineHeight = 2.5;
      const totalTextHeight = lines.length * lineHeight;
      const startY = this.currentY + (headerHeight - totalTextHeight) / 2 + lineHeight;

      let headerY = startY;
      lines.forEach(line => {
        // Center text horizontally in each column
        const textX = colX[i] + col.width / 2;
        this.doc.text(line, textX, headerY, { align: 'center' });
        headerY += lineHeight;
      });
    });

    this.currentY += headerHeight;

    // Draw horizontal line after header
    this.doc.setLineWidth(0.3);
    this.doc.line(startX, this.currentY, startX + tableWidth, this.currentY);

    // Draw items
    this.doc.setFont('helvetica', 'normal');
    this.data.items.forEach((item) => {
      // Description (may wrap)
      const descLines = this.doc.splitTextToSize(item.description, columns[1].width - 3);
      const rowHeight = Math.max(8, descLines.length * 4 + 2);

      // Check if we need a new page
      if (this.currentY + rowHeight > this.pageHeight - 60) {
        this.doc.addPage();
        this.currentY = this.margin + 10;
        this.drawBorder();
      }

      const rowY = this.currentY + 3.5;

      // S.No (centered)
      this.doc.text(item.sno.toString(), colX[0] + columns[0].width / 2, rowY, { align: 'center' });

      // Description (wrapped)
      this.doc.text(descLines, colX[1] + 1, rowY);

      // HSN
      this.doc.text(item.hsn || '', colX[2] + 1, rowY);

      // Qty with unit
      this.doc.text(`${item.qty.toFixed(2)}`, colX[3] + 1, rowY);
      this.doc.setFontSize(7);
      this.doc.text(`${item.unit}`, colX[3] + 1, rowY + 3);
      this.doc.setFontSize(8);

      // Rate
      this.doc.text(item.rate.toFixed(2), colX[4] + columns[4].width - 2, rowY, { align: 'right' });

      // CGST %
      this.doc.text(`${item.cgstPercent}%`, colX[5] + 1, rowY);

      // CGST Amt
      this.doc.text(item.cgstAmount.toFixed(2), colX[6] + columns[6].width - 2, rowY, { align: 'right' ,});

      // SGST %
      this.doc.text(`${item.sgstPercent}%`, colX[7] + 1, rowY);

      // SGST Amt
      this.doc.text(item.sgstAmount.toFixed(2), colX[8] + columns[8].width - 2, rowY, { align: 'right' });

      // Amount (right aligned)
      this.doc.text(item.amount.toFixed(2), colX[9] + columns[9].width - 2, rowY, { align: 'right' });

      this.currentY += rowHeight;
      this.doc.line(startX, this.currentY, startX + tableWidth, this.currentY);
    });

    // Draw vertical lines for all columns
    columns.forEach((_col, i) => {
      this.doc.line(colX[i], startY, colX[i], this.currentY);
    });
    // Right border
    this.doc.line(colX[columns.length - 1] + columns[columns.length - 1].width, startY, colX[columns.length - 1] + columns[columns.length - 1].width, this.currentY);
  }

  private drawTotals() {
    this.currentY += 5;
    const leftX = this.margin + 5;
    const rightLabelX = this.pageWidth - 70;
    const rightValueX = this.pageWidth - this.margin - 5;

    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'normal');

    const totalsStartY = this.currentY;

    // Items in Total (left side)
    const totalQty = this.data.items.reduce((sum, item) => sum + item.qty, 0);
    this.doc.text(`Items in Total ${totalQty.toFixed(2)}`, leftX, this.currentY);

    // Totals on right side
    const rightY = totalsStartY;
    this.drawRightAlignedRow('Sub Total', this.data.subTotal.toFixed(2), rightLabelX, rightValueX, rightY);
    this.drawRightAlignedRow('CGST9 (9%)', this.data.cgstTotal.toFixed(2), rightLabelX, rightValueX, rightY + 4);
    this.drawRightAlignedRow('SGST9 (9%)', this.data.sgstTotal.toFixed(2), rightLabelX, rightValueX, rightY + 8);

    this.doc.setFont('helvetica', 'bold');
    this.drawRightAlignedRow('Total', this.data.grandTotal.toFixed(2), rightLabelX, rightValueX, rightY + 13);

    this.currentY = rightY + 18;
    this.doc.setLineWidth(0.3);
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);

    // Total in words
    this.currentY += 4;
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Total In Words', leftX, this.currentY);
    this.currentY += 4;
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(this.data.totalInWords, leftX, this.currentY);

    this.currentY += 4;
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
  }

  private drawNotesAndBank() {
    this.currentY += 4;
    const leftX = this.margin + 5;
    const rightX = this.pageWidth - this.margin - 60;

    this.doc.setFontSize(9);

    // Notes section (if provided)
    if (this.data.notes) {
      this.doc.setFont('helvetica', 'bold');
      this.doc.text('**Notes', leftX, this.currentY);
      this.currentY += 4;
      this.doc.setFont('helvetica', 'normal');
      this.doc.text(this.data.notes, leftX, this.currentY);
      this.currentY += 5;
    }

    // Bank Details (left side)
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(this.data.bankDetails.bankName, leftX, this.currentY);
    this.currentY += 4;
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`AC.NO NEW- : ${this.data.bankDetails.accountNumber}`, leftX, this.currentY);
    this.currentY += 4;
    this.doc.text(`IFSC CODE : ${this.data.bankDetails.ifscCode}`, leftX, this.currentY);
    this.currentY += 4;
    this.doc.text(`BRANCH : ${this.data.bankDetails.branch}`, leftX, this.currentY);

    // Authorized Signatory (right side, aligned with bank details bottom)
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`For ${this.data.companyName}`, rightX, this.currentY);

    this.currentY += 5;
    this.doc.setLineWidth(0.3);
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
  }

  private drawFooter() {
    this.currentY += 4;
    const leftX = this.margin + 5;

    // Terms & Conditions
    this.doc.setFontSize(9);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text('Terms & Conditions', leftX, this.currentY);
    this.currentY += 4;
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(8);

    const tcText = this.data.termsAndConditions ||
      'We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct';
    const tcLines = this.doc.splitTextToSize(tcText, this.pageWidth - 2 * this.margin - 10);
    this.doc.text(tcLines, leftX, this.currentY);
    this.currentY += tcLines.length * 3.5 + 2;

    this.doc.setLineWidth(0.3);
    this.doc.line(this.margin, this.currentY, this.pageWidth - this.margin, this.currentY);
  }

  private drawReceiverSection() {
    this.currentY += 4;
    const leftX = this.margin + 5;

    this.doc.setFontSize(10);
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(`RECEIVER@ ${this.data.billTo.name}`, leftX, this.currentY);

    this.currentY += 6;
    this.doc.setFont('helvetica', 'normal');
    this.doc.setFontSize(9);

    // Signature, Name, Mobile on same line with proper spacing
    const sigX = leftX + 5;
    const nameX = leftX + 65;
    const mobileX = leftX + 120;

    this.doc.text('Signature :____________', sigX, this.currentY);
    this.doc.text('Name:____________', nameX, this.currentY);
    this.doc.text('Mobile:____________', mobileX, this.currentY);
  }

  private drawLabelValue(label: string, value: string, x: number, y: number) {
    this.doc.setFont('helvetica', 'bold');
    this.doc.text(`${label}`, x, y);
    this.doc.setFont('helvetica', 'normal');
    this.doc.text(`: ${value}`, x + 28, y);
  }

  private drawRightAlignedRow(label: string, value: string, labelX: number, valueX: number, y: number) {
    this.doc.text(label, labelX, y);
    this.doc.text(value, valueX, y, { align: 'right' });
  }

  download(filename: string = 'invoice.pdf') {
    this.doc.save(filename);
  }

  preview(): string {
    return this.doc.output('dataurlstring');
  }

  getBlob(): Blob {
    return this.doc.output('blob');
  }
}
